'use client';
// Canal de suggestion du personnel — ISO 15189:2022 §8.6.
//
// Le retour des PATIENTS avait son canal (réclamations, §7.7). Celui du
// PERSONNEL n'en avait aucun : c'est le vieux widget « voix du personnel »,
// différé depuis le premier lot du module Qualité.
//
// Le DÉPÔT ne se fait pas ici. Il se fait depuis la gommette, en bas à droite
// de tous les écrans des trois modules — le même patron que la déclaration de
// non-conformité, pour la même raison : ce qui ne se dépose pas en vingt
// secondes ne se dépose jamais. Cet écran est celui du TRAITEMENT.
//
// Trois choses qu'il tient :
//
//  · L'ANONYMAT EST TENU. Quand le dépôt est anonyme, le dépositaire n'apparaît
//    nulle part — pas même pour le RQ, pas même en revue de direction. Un
//    anonymat proposé au dépôt puis levé au traitement serait pire que pas
//    d'anonymat du tout.
//
//  · RÉPONDRE EST OBLIGATOIRE POUR ÉCARTER. « Non retenue » sans motif est la
//    façon la plus sûre de fermer le canal : une suggestion sans réponse tue
//    les dix suivantes.
//
//  · CE N'EST PAS UNE NON-CONFORMITÉ. Une suggestion n'a ni gravité, ni analyse
//    de cause, ni impact sur des résultats. Versée au registre des NC, elle
//    gonflerait un compteur que la direction lit comme un indicateur de
//    défaillance. Elle se porte au plan d'actions en nature « amélioration » —
//    et c'est exactement la preuve que le §8.6 vient chercher : une action
//    d'amélioration NON issue d'une non-conformité.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed, userName } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { Skeleton,
  I, card, inputStyle, EmptyState, FilterBar, RefISO,
  FieldError, useBusy, Modale } from '@/components/ui';
import { EnteteEcran } from '@/components/EnteteEcran';
import { JournalFiche } from '@/components/JournalFiche';
import { H1, lbl } from './ressources/commun';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
type Ligne = {
  documentId: string; ref: string; titre: string; description: string | null;
  anonyme: boolean; deposePar: string; dateDepot: string;
  origineEcran: string | null; domaine: string | null;
  statut: string; statutLabel: string;
  reponse: string | null; repondPar: string | null; dateReponse: string;
  actionRef: string | null;
  /** Statut de l'action portée au plan, et le raccourci qui en découle. Une
   *  action ANNULÉE ne condamne pas la suggestion : le lien reste visible, mais
   *  le passage au plan redevient offert. */
  actionStatut?: string;
  actionAnnulee?: boolean;
  /** L'action citée n'existe pas au plan — référence de reprise jamais créée. */
  actionIntrouvable?: boolean;
};
type Dossier = {
  lignes: Ligne[];
  statuts: { code: string; label: string }[];
  peutTraiter: boolean;
  compte: {
    total: number; enAttente: number; sansReponse: number;
    retenues: number; nonRetenues: number; porteesAuPlan: number; anonymes: number;
  };
};

const COULEUR: Record<string, { bg: string; fg: string }> = {
  deposee: { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  lue: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  retenue: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
  non_retenue: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  cloturee: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

/** Traitement d'une suggestion : la réponse, le statut, et le passage au plan. */
function Traitement({ ligne, statuts, onFait, onAnnule }: {
  ligne: Ligne; statuts: { code: string; label: string }[];
  onFait: () => void; onAnnule: () => void;
}) {
  const [statut, setStatut] = useState(ligne.statut);
  const [reponse, setReponse] = useState(ligne.reponse || '');
  const [domaine, setDomaine] = useState(ligne.domaine || '');
  /* Ce que l'écran a lu, figé à l'ouverture (14/10/2026) : le moteur n'écrit
     que ce qui a changé depuis, et refuse (409) ce qu'un autre a changé. */
  const [base] = useState(() => ({ statut: ligne.statut, reponse: ligne.reponse || '', domaine: ligne.domaine || '' }));
  const [echeance, setEcheance] = useState('');
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const { showToast, naviguerVers } = useGed();

  const enregistrer = () => run('save', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/suggestions/${ligne.documentId}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ statut, reponse, domaine, base }),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  /* ⚠️ LA SAISIE PART AVEC LE PASSAGE AU PLAN (07/10/2026).
     Ce bouton referme la fenêtre et recharge la liste. Il n'envoyait que
     l'échéance : la réponse au dépositaire et le domaine tapés juste avant,
     sans « Enregistrer », étaient perdus SANS UN MOT. On les joint donc à
     l'appel — le serveur les écrit dans la même mise à jour que la référence
     d'action, et n'efface rien s'ils sont vides. */
  /* ⚠️ ET LE PASSAGE SE DIT, AVEC LA RÉFÉRENCE ET LE CHEMIN (RQ, 11/10/2026).
     Relevé tel quel : « j'ai cliqué sur plan d'action mais je ne sais pas où il
     est apparu ou parti. » La route rendait pourtant `actionRef` — la réponse
     était simplement JETÉE, la fenêtre se refermait, et la seule trace visible
     était une petite mention verte sur la ligne, qu'on ne regarde pas puisqu'on
     vient de fermer la fenêtre.

     Le fait créé est donc ANNONCÉ : sa référence, et le bouton qui y mène. Le
     toast porte une action depuis le 13/09/2026 (`showToast`), et c'est
     exactement l'usage pour lequel elle existe — une notification qui mène à la
     tâche, et non à une liste. */
  const porterAuPlan = () => run('plan', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/suggestions/${ligne.documentId}/action`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ echeance: echeance || undefined, reponse, domaine }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) {
      setErreur(j?.error?.message || 'Passage au plan refusé.');
      return;
    }
    const ref = String(j?.actionRef || '');
    showToast(
      ref
        ? `Plan d’actions : ${ref} enclenchée depuis ${ligne.ref}.`
        : 'Suggestion portée au plan d’actions.',
      'info',
      /* ⚠️ On navigue vers l'ÉCRAN, pas vers la fiche : la route ne rend que la
         référence métier, et `cle`/`valeur` attendent un `documentId`. Désigner
         une fiche avec la mauvaise clé l'ouvrirait sur un vide — un bouton qui
         dépose au mauvais endroit est pire que pas de bouton. */
      { label: 'Voir le plan d’actions', faire: () => naviguerVers('actions') },
    );
    onFait();
  });

  return (
    <Modale
      titre={`${ligne.ref} — ${ligne.titre}`}
      sousTitre={`Déposée le ${fmtDate(ligne.dateDepot)}${ligne.anonyme ? ' · dépôt anonyme' : ''}`}
      onClose={onAnnule}
      largeur={760}
      pied={(
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
          {(!ligne.actionRef || ligne.actionAnnulee || ligne.actionIntrouvable) && (
            <>
              <label htmlFor="s-ech" style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 600 }}>Échéance</label>
              <input id="s-ech" type="date" value={echeance} onChange={(e) => setEcheance(e.target.value)}
                style={{ ...inputStyle, width: 150 }} />
              <Bouton onClick={() => void porterAuPlan()} disabled={estOccupe()}
                title="Créer une action d’AMÉLIORATION au plan d’actions. C’est ce passage qui rend la suggestion exigible — et c’est la preuve que le §8.6 vient chercher. La réponse et le domaine saisis sont enregistrés au passage, et la suite donnée devient « Retenue ».">
                {estOccupe('plan') ? 'Passage…' : (ligne.actionAnnulee ? '→ plan d’actions (nouvelle)' : '→ plan d’actions')}
              </Bouton>
            </>
          )}
          <span style={{ flex: 1 }} />
          <PiedActions annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
        </div>
      )}
    >
    <div style={{ display: 'grid', gap: 12 }}>
      {ligne.description && (
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{ligne.description}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="s-statut">Suite donnée</label>
          <select id="s-statut" value={statut} onChange={(e) => setStatut(e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            {statuts.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl} htmlFor="s-domaine">Domaine <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(facultatif)</span></label>
          <input id="s-domaine" value={domaine} maxLength={80} onChange={(e) => setDomaine(e.target.value)}
            placeholder="Organisation, sécurité, matériel…" style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
      </div>

      <div>
        <label style={lbl} htmlFor="s-rep">Réponse au dépositaire</label>
        <textarea id="s-rep" value={reponse} rows={3} onChange={(e) => setReponse(e.target.value)}
          placeholder="Même « non » se répond. Écarter une suggestion sans dire pourquoi est la façon la plus sûre de fermer le canal."
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>

      <FieldError>{erreur}</FieldError>

      {ligne.actionRef && !ligne.actionAnnulee && !ligne.actionIntrouvable && (
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', background: 'var(--green-50)', borderRadius: 'var(--radius-sm)', padding: '8px 12px' }}>
          Déjà portée au plan d’actions sous <strong style={{ fontFamily: 'var(--font-mono)' }}>{ligne.actionRef}</strong>.
          Une suggestion ne s’y porte qu’une fois : le plan ne doit pas compter deux fois la même amélioration.
        </div>
      )}
      {/* ⚠️ L'ACTION CITÉE N'EXISTE PAS AU PLAN (relevé MAJ-06). Trois
          suggestions renvoient vers des références de reprise jamais créées.
          L'écran affichait « Déjà portée au plan d'actions sous … » — une
          phrase fausse ET rassurante, qui laissait croire qu'une suite avait
          été donnée et masquait le bouton permettant d'en donner une. */}
      {ligne.actionIntrouvable && (
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', background: 'var(--amber-50)', border: '1px solid var(--amber-300)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', lineHeight: 1.5 }}>
          Cette fiche cite l’action <strong style={{ fontFamily: 'var(--font-mono)' }}>{ligne.actionRef}</strong>,
          qui <strong>ne figure pas au plan d’actions</strong>. La référence vient probablement de la reprise des
          données ; rien n’atteste qu’une action a été menée. La suggestion peut être portée au plan.
        </div>
      )}
      {/* L'action annulée n'est PAS effacée de la fiche : elle a existé, et le
          journal la porte. Ce qu'on rend, c'est la possibilité d'en créer une
          autre — une annulation ne doit pas geler la suggestion. */}
      {ligne.actionAnnulee && (
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', lineHeight: 1.5 }}>
          L’action <strong style={{ fontFamily: 'var(--font-mono)' }}>{ligne.actionRef}</strong> qui portait cette
          suggestion a été <strong>annulée</strong>. Le lien reste — il fait partie de l’histoire de la fiche —
          mais la suggestion peut être portée au plan à nouveau.
        </div>
      )}

      {/* Le journal de la fiche, à l'endroit où on se pose la question — pas
          dans un écran séparé qu'il faudrait penser à ouvrir. Les événements
          étaient écrits et chaînés depuis toujours ; seul l'affichage manquait. */}
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
        <JournalFiche code={ligne.ref} compact />
      </div>
    </div>
    </Modale>
  );
}

export function Suggestions() {
  const { settings } = useGed();
  const [d, setD] = useState<Dossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [ouverte, setOuverte] = useState<Ligne | null>(null);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/suggestions', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const recharger = () => { setOuverte(null); setTour((t) => t + 1); };

  const filtrees = useMemo(() => {
    const base = (d?.lignes || []).filter((l) => (
      filtre === 'attente' ? ['deposee', 'lue'].includes(l.statut)
        : filtre === 'sansreponse' ? !String(l.reponse || '').trim()
          : filtre === 'retenues' ? l.statut === 'retenue'
            : filtre === 'plan' ? !!l.actionRef
              : true));
    const qq = q.trim().toLowerCase();
    return qq
      ? base.filter((l) => `${l.ref} ${l.titre} ${l.description} ${l.domaine}`.toLowerCase().includes(qq))
      : base;
  }, [d, filtre, q]);

  /* `Skeleton` et non une phrase grise (24/08/2026) : l'attente se dit de la
     même façon sur les 32 autres écrans, et le gabarit annonce la FORME de ce
     qui arrive au lieu de laisser la page vide. */
  if (chargement) return <Skeleton rows={5} />;
  if (!d) return null;
  const { compte, peutTraiter } = d;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <EnteteEcran rubrique="Amélioration" clauseISO="8.6" titre="Suggestions du personnel" />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        {/* ⚠️ CITER LE LIBELLÉ EXACT (relevé DAT-11 du 26/08/2026). L'écran
            parlait de « la gommette en bas de l'écran » ; le bouton s'appelle
            « Une idée ? ». Le mot « gommette » est un terme de conception, pas
            un mot du laboratoire : on ne le lit nulle part à l'écran, donc on
            cherche un objet qui n'existe pas sous ce nom. Une consigne qui
            nomme autrement ce qu'elle désigne ne se suit pas. */}
        Le dépôt se fait par le bouton <strong>« Une idée ? »</strong>, en bas à droite de l’écran, depuis n’importe
        quelle page des trois modules — deux champs, sans rien qualifier, comme pour une
        non-conformité. Cet écran est celui du traitement. Une suggestion retenue se porte au
        plan d’actions en nature <strong>amélioration</strong> : c’est ce passage qui la rend
        exigible.
      </p>

      {compte.sansReponse > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong>{compte.sansReponse} suggestion{compte.sansReponse > 1 ? 's' : ''} sans réponse.</strong>{' '}
          C’est le seul chiffre qui dit si le canal vit. Une suggestion sans réponse tue les dix
          suivantes — et « non, parce que… » est une réponse.
        </div>
      )}

      {compte.total === 0 && (
        <EmptyState
          titre="Aucune suggestion n’a encore été déposée."
          indice="Le canal est ouvert : la gommette en bas à droite de chaque écran permet de déposer en deux champs, anonymement si on le souhaite. Un canal vide n’est pas un défaut — c’est un canal dont il reste à vérifier qu’il est connu du personnel." />
      )}

      {compte.total > 0 && (
        <>
          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher une suggestion…"
            chips={[
              { k: 'tous', label: 'Toutes', n: compte.total },
              { k: 'attente', label: 'À traiter', n: compte.enAttente },
              { k: 'sansreponse', label: 'Sans réponse', n: compte.sansReponse },
              { k: 'retenues', label: 'Retenues', n: compte.retenues },
              { k: 'plan', label: 'Portées au plan', n: compte.porteesAuPlan },
            ]}
            actif={filtre} onChip={setFiltre}
            total={compte.total} affiches={filtrees.length}
            onReset={() => { setQ(''); setFiltre('tous'); }} />

          {ouverte && peutTraiter && (
            <Traitement ligne={ouverte} statuts={d.statuts} onFait={recharger} onAnnule={() => setOuverte(null)} />
          )}

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {filtrees.map((l, i) => {
              const c = COULEUR[l.statut] || COULEUR.deposee;
              return (
                /* Toute la ligne ouvre la fiche. Un bouton « Revoir » au bout
                   d'un bloc de six lignes demande de viser une cible de
                   soixante pixels alors que la zone cliquable évidente fait
                   toute la largeur — et la RQ visait le bouton. Rendu comme un
                   <button> et non un <div onClick> : le clavier et les
                   lecteurs d'écran le trouvent, et un div cliquable ne se
                   tabule pas. */
                <button
                  key={l.documentId}
                  type="button"
                  onClick={() => { if (peutTraiter) setOuverte(l); }}
                  disabled={!peutTraiter}
                  className={peutTraiter ? 'ged-row' : undefined}
                  aria-label={`Ouvrir ${l.ref} — ${l.titre}`}
                  style={{ display: 'block', width: '100%', textAlign: 'start', font: 'inherit', fontFamily: 'var(--font-sans)', background: 'none', border: 'none', borderTop: i ? '1px solid var(--border-subtle)' : 'none', padding: '11px 14px', cursor: peutTraiter ? 'pointer' : 'default' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.ref}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{l.titre}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: c.bg, color: c.fg }}>{l.statutLabel}</span>
                    {l.actionRef && (
                      <span
                        title={l.actionAnnulee ? 'Action annulée — la suggestion peut être portée au plan à nouveau' : undefined}
                        style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: l.actionAnnulee ? 'var(--text-muted)' : 'var(--green-600)', textDecoration: l.actionAnnulee ? 'line-through' : 'none' }}>
                        → {l.actionRef}
                      </span>
                    )}
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      {/* L'anonymat promis au dépôt est tenu ici, y compris
                          pour le RQ : le lever au traitement serait pire que
                          de ne jamais l'avoir proposé. */}
                      {l.anonyme ? 'dépôt anonyme' : userName(settings, l.deposePar)} · {fmtDate(l.dateDepot)}
                    </span>
                  </div>

                  {l.description && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 4, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{l.description}</div>
                  )}

                  {l.reponse ? (
                    <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 6, lineHeight: 1.5, borderInlineStart: '2px solid var(--green-600)', paddingInlineStart: 9 }}>
                      <strong style={{ color: 'var(--text-strong)' }}>Réponse</strong>
                      {l.repondPar ? ` — ${userName(settings, l.repondPar)}` : ''}
                      {l.dateReponse ? `, ${fmtDate(l.dateReponse)}` : ''} : {l.reponse}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 700, marginTop: 5 }}>
                      Pas encore de réponse.
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 12, marginTop: 5, fontSize: 12.5, color: 'var(--text-muted)', flexWrap: 'wrap', alignItems: 'center' }}>
                    {l.domaine && <span>{l.domaine}</span>}
                    {l.origineEcran && <span>déposée depuis « {l.origineEcran} »</span>}
                    <span style={{ flex: 1 }} />
                    {peutTraiter && (
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>
                        {l.reponse ? 'Ouvrir' : 'Répondre'} →
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
            {filtrees.length === 0 && (
              <div style={{ padding: 16 }}>
                <EmptyState titre="Aucune suggestion ne correspond à ces critères."
                  action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser</Bouton>} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
